import React, { useState } from 'react';
import { Trash2, FileText, Copy, AlertTriangle, Clock, Pause, Play, Info, RotateCw } from 'lucide-react';
import { PrintJobDto, printingApi } from '../../../services/api';
import { PrintJobStatusBadge } from './PrintJobStatusBadge';

interface PrintJobItemProps {
  job: PrintJobDto;
  onDelete: (jobId: string) => Promise<void>;
  onUpdate?: () => void;
}

export const PrintJobItem: React.FC<PrintJobItemProps> = ({ job, onDelete, onUpdate }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const id = job.id || job.jobId;
      if (!id) {
        console.error('No job ID found');
        return;
      }
      await onDelete(id);
      setShowConfirm(false);
    } catch (error) {
      console.error('Error deleting job:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePause = async () => {
    try {
      setIsPausing(true);
      const id = job.id || job.jobId;
      if (!id) return;
      await printingApi.pauseJob(id);
      onUpdate?.();
    } catch (error: any) {
      console.error('Error pausing job:', error);
      alert(error.message || 'Error al pausar el trabajo');
    } finally {
      setIsPausing(false);
    }
  };

  const handleResume = async () => {
    try {
      setIsResuming(true);
      const id = job.id || job.jobId;
      if (!id) return;
      await printingApi.resumeJob(id);
      onUpdate?.();
    } catch (error: any) {
      console.error('Error resuming job:', error);
      alert(error.message || 'Error al reanudar el trabajo');
    } finally {
      setIsResuming(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return 'Ahora mismo';
      if (diffMins < 60) return `Hace ${diffMins} min`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `Hace ${diffHours}h`;
      const diffDays = Math.floor(diffHours / 24);
      return `Hace ${diffDays}d`;
    } catch {
      return dateString;
    }
  };

  // Determine available actions based on status
  const canPause = job.status === 'Printing';
  const canResume = job.status === 'Paused';
  const canDelete = ['Completed', 'Failed', 'Cancelled'].includes(job.status);
  const canCancel = ['Pending', 'Claimed'].includes(job.status);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Mobile Layout */}
      <div className="md:hidden space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileText size={16} className="text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 text-sm truncate">{job.fileName}</p>
              <p className="text-xs text-slate-500 mt-0.5">{getTimeAgo(job.createdAt)}</p>
            </div>
          </div>
          <PrintJobStatusBadge status={job.status} />
        </div>

        {/* Status Message */}
        {job.statusMessage && (
          <div className="bg-green-50 border border-green-200 rounded p-2 flex items-start gap-2">
            <Info size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-green-700">{job.statusMessage}</p>
          </div>
        )}

        {/* Retry Attempt */}
        {job.status === 'Retrying' && job.retryAttempt !== undefined && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 flex items-center gap-2">
            <RotateCw size={14} className="text-yellow-600" />
            <p className="text-xs text-yellow-700">
              Reintento {job.retryAttempt}/{job.maxAttempts || 3}
            </p>
          </div>
        )}

        <div className="flex items-center gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1">
            <Copy size={12} />
            <span>{job.copies} {job.copies === 1 ? 'copia' : 'copias'}</span>
          </div>
          {job.priority > 0 && (
            <div className="flex items-center gap-1">
              <AlertTriangle size={12} className="text-orange-500" />
              <span>Prioridad {job.priority}</span>
            </div>
          )}
        </div>

        {(job.error || job.lastError) && (
          <div className="bg-red-50 border border-red-200 rounded p-2">
            <p className="text-xs text-red-700 font-medium">Error:</p>
            <p className="text-xs text-red-600 mt-1">{job.error || job.lastError}</p>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-slate-100 gap-2">
          <div className="text-xs text-slate-500">
            {job.attempts > 1 && <span>{job.attempts} intentos</span>}
          </div>
          <div className="flex gap-2">
            {canPause && (
              <button
                onClick={handlePause}
                disabled={isPausing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Pause size={14} />
                Pausar
              </button>
            )}
            {canResume && (
              <button
                onClick={handleResume}
                disabled={isResuming}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Play size={14} />
                Reanudar
              </button>
            )}
            {(canDelete || canCancel) && (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={isDeleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 size={14} />
                {canCancel ? 'Cancelar' : 'Eliminar'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop/Tablet Layout */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText size={18} className="text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 truncate">{job.fileName}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  <span>{getTimeAgo(job.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Copy size={12} />
                  <span>{job.copies} {job.copies === 1 ? 'copia' : 'copias'}</span>
                </div>
                {job.priority > 0 && (
                  <div className="flex items-center gap-1 text-orange-600">
                    <AlertTriangle size={12} />
                    <span>Prioridad {job.priority}</span>
                  </div>
                )}
                {job.attempts > 1 && <span>{job.attempts} intentos</span>}
                {job.status === 'Retrying' && job.retryAttempt !== undefined && (
                  <span className="text-yellow-600 font-medium">
                    Reintento {job.retryAttempt}/{job.maxAttempts || 3}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <PrintJobStatusBadge status={job.status} />
            {canPause && (
              <button
                onClick={handlePause}
                disabled={isPausing}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Pause size={16} />
                <span className="hidden lg:inline">Pausar</span>
              </button>
            )}
            {canResume && (
              <button
                onClick={handleResume}
                disabled={isResuming}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Play size={16} />
                <span className="hidden lg:inline">Reanudar</span>
              </button>
            )}
            {(canDelete || canCancel) && (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={isDeleting}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 size={16} />
                <span className="hidden lg:inline">{canCancel ? 'Cancelar' : 'Eliminar'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Status Message (Desktop) */}
        {job.statusMessage && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
            <Info size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-700">{job.statusMessage}</p>
          </div>
        )}

        {/* Error Message (Desktop) */}
        {(job.error || job.lastError) && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-700 font-medium">Error:</p>
            <p className="text-xs text-red-600 mt-1">{job.error || job.lastError}</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Confirmar {canCancel ? 'cancelación' : 'eliminación'}
                </h3>
                <p className="text-sm text-slate-600">Esta acción no se puede deshacer</p>
              </div>
            </div>
            <p className="text-sm text-slate-700 mb-6">
              ¿Estás seguro de que quieres {canCancel ? 'cancelar' : 'eliminar'} el trabajo <span className="font-medium">"{job.fileName}"</span>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {canCancel ? 'Cancelando...' : 'Eliminando...'}
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    {canCancel ? 'Cancelar' : 'Eliminar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
